import axios from "axios";
import axiosInstance from "../../axiosInstance";

const BASE_URL = "/elearning/documents";

/** File >= ngưỡng này: PUT trực tiếp lên GCS (signed URL), không qua body Node — nhanh hơn với file lớn */
export const DIRECT_UPLOAD_MIN_BYTES = 10 * 1024 * 1024;

// Lấy danh sách tài liệu (phân trang + lọc server-side)
/** @param {{ page?: number, size?: number, keyword?: string, subject?: string, type?: string, extension?: string, is_active?: string }} [params] */
export const getAllDocuments = async (params = {}) => {
  const response = await axiosInstance.get(BASE_URL, { params });
  const data = response.data;
  if (Array.isArray(data)) {
    return {
      items: data,
      total: data.length,
      page: 1,
      size: data.length,
      stats: null,
    };
  }
  return {
    items: data?.items || [],
    total: Number(data?.total) || 0,
    page: Number(data?.page) || 1,
    size: Number(data?.size) || 20,
    stats: data?.stats || null,
  };
};

/** @param {{ status?: string|boolean, level?: string, course_id?: string, subject?: string, scope?: string }} filters */
export const getDocumentsByStatusAndLevel = async ({
  status,
  level,
  course_id,
  subject,
  scope,
} = {}) => {
  const params = new URLSearchParams();
  if (status !== undefined && status !== null && status !== "") {
    const on = status === true || status === "true" || status === "1";
    params.append("is_active", on ? "true" : "false");
  }
  if (level) params.append("level", level);
  if (course_id) params.append("course_id", course_id);
  if (subject) params.append("subject", subject);
  if (scope) params.append("scope", scope);

  const res = await axiosInstance.get(`${BASE_URL}/filter?${params.toString()}`);
  return res.data;
};

async function uploadDocumentDirectToGcs(formData, opts = {}) {
  const { onProgress, signal } = opts;
  const file = formData.get("file");
  const file_name = formData.get("file_name");
  const file_type = formData.get("file_type") || "application/octet-stream";
  const file_extension = formData.get("file_extension") || "";
  const file_size = formData.get("file_size");
  const type = formData.get("type");
  const subject = formData.get("subject");
  const course_id = formData.get("course_id");
  const description = formData.get("description");
  const is_active = formData.get("is_active");

  const initRes = await axiosInstance.post(`${BASE_URL}/upload/direct/init`, {
    file_type,
    file_extension,
    type,
    file_size: file_size != null ? String(file_size) : String(file?.size ?? 0),
  });

  const { uploadUrl, filePath, contentType } = initRes.data;

  await axios.put(uploadUrl, file, {
    headers: {
      "Content-Type": contentType || file_type || "application/octet-stream",
    },
    signal,
    timeout: 90 * 60_000,
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
    onUploadProgress: (evt) => {
      const loaded = evt?.loaded ?? 0;
      const total = evt?.total ?? 0;
      onProgress?.({ loaded, total });
    },
  });

  const completeRes = await axiosInstance.post(`${BASE_URL}/upload/direct/complete`, {
    file_path: filePath,
    file_name,
    file_type,
    file_extension,
    file_size: file_size != null ? String(file_size) : String(file?.size ?? 0),
    description,
    is_active,
    type,
    subject,
    course_id,
  });

  return completeRes.data;
}

export const uploadDocument = async (formData, opts = {}) => {
  const file = formData.get("file");
  // File >= 10MB (bao gồm bài giảng .zip): PUT trực tiếp lên Google Cloud Storage (Signed URL)
  // Tránh timeout 100s của Render và tránh giới hạn 100MB của Cloudflare
  if (
    file &&
    typeof file.size === "number" &&
    file.size >= DIRECT_UPLOAD_MIN_BYTES
  ) {
    return uploadDocumentDirectToGcs(formData, opts);
  }

  const { onProgress, signal } = opts;

  // Khớp tối đa với BE: timeout upload động tới ~90 phút cho file lớn
  const res = await axiosInstance.post(`${BASE_URL}/upload`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    signal,
    timeout: 90 * 60_000,
    onUploadProgress: (evt) => {
      const loaded = evt?.loaded ?? 0;
      const total = evt?.total ?? 0;
      onProgress?.({ loaded, total });
    },
  });
  return res.data;
};

/** Tránh gọi /view trùng request khi đang chờ (Strict Mode mount effect 2 lần ở dev). */
const pendingViewByKey = new Map();

/**
 * @param {string} id
 * @param {{ signal?: AbortSignal, onDownloadProgress?: (evt: import("axios").AxiosProgressEvent) => void, action?: "view"|"download" }} [opts]
 */
export const viewDocument = async (id, opts = {}) => {
  const action = opts.action === "download" ? "download" : "view";
  const key = `${id}:${action}`;
  const inflight = pendingViewByKey.get(key);
  if (inflight) return inflight;

  const p = axiosInstance
    .get(`${BASE_URL}/${id}/view`, {
      responseType: "blob",
      signal: opts.signal,
      onDownloadProgress: opts.onDownloadProgress,
      params: { action },
    })
    .finally(() => {
      pendingViewByKey.delete(key);
    });

  pendingViewByKey.set(key, p);
  return p;
};

/** Mở tài liệu trong tab mới (có Bearer token qua axios) */
export const openDocumentInNewTab = async (id) => {
  const res = await viewDocument(id);
  const blob = res.data;
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank", "noopener,noreferrer");
  if (!win) {
    URL.revokeObjectURL(url);
    throw new Error("Trình duyệt đã chặn cửa sổ mới. Vui lòng cho phép popup.");
  }
  setTimeout(() => URL.revokeObjectURL(url), 180000);
};

export const patchDocument = async (id, body) => {
  const res = await axiosInstance.patch(`${BASE_URL}/${id}`, body);
  return res.data;
};

export const deleteDocument = async (id) => {
  const res = await axiosInstance.delete(`${BASE_URL}/${id}`);
  return res.data;
};
